import type { Els, State } from "@/types";
import { loadStoredPlainApiKeys } from "@/utils";

// ===== 全局状态(可变单例) =====

export const state: State = {
  members: [],
  requestLogs: [],
  requestLogsLoaded: false,
  requestLogsLoading: false,
  todayErrorRequests: [],
  todayErrorRequestsOpen: false,
  todayErrorRequestsLoading: false,
  todayErrorRequestsPagination: { page: 1, pageSize: 20, total: 0, totalPages: 1 },
  currentView: "team",
  pendingApiKeyMemberId: "",
  pendingApiKeyIsUser: false,
  expandedMemberIds: new Set<string>(),
  plainApiKeys: loadStoredPlainApiKeys(),
  pagination: {
    members: { page: 1, pageSize: 10 },
    requestLogs: { page: 1, pageSize: 10 },
    users: { page: 1, pageSize: 10 }
  },
  user: null,
  csrfToken: "",
  userApiKeys: [],
  // 路由
  route: "home",
  appTab: "overview",
  authTab: "login",
  // admin 新增
  adminUsers: [],
  adminInteractionCapturePolicy: null,
  adminChannels: [],
  adminChannelOverrides: [],
  adminProviderInstances: [],
  adminBridgeInstances: [],
  adminBridgeFailoverEvents: [],
  adminChannelTab: "channels",
  adminUsersSearch: "",
  adminSelectedUserId: null,
  adminUserDetailTab: "usage",
  adminModelUsagePeriod: "total",
  adminKeyDrawer: null,
  adminWalletTransactions: [],
  adminWalletTransactionSummary: null,
  adminWalletTransactionsLoaded: false,
  adminWalletTransactionsLoading: false,
  adminWalletFilters: { memberId: "", from: "", to: "", type: "" },
  adminRechargeRevenue: null,
  adminHealth: null,
  adminOauth: null,
  appDashboard: null,
  appModelUsage: null,
  appModelUsagePeriod: "total",
  appUsageRecords: [],
  appUsagePagination: { page: 1, pageSize: 20, total: 0, totalPages: 1 },
  appWallet: null,
  appEntitlements: null,
  appFundingSummary: null,
  appWalletTransactions: [],
  appWalletFilters: { memberId: "", from: "", to: "", type: "" },
  appWalletPagination: { page: 1, pageSize: 20, total: 0, totalPages: 1 },
  appOrders: [],
  // 聊天
  chatSessions: [],
  chatCurrentSessionId: "",
  chatMessages: [],
  chatQuota: null,
  chatAvailableModels: [],
  chatSelectedModel: "",
  chatStreaming: false,
  chatStreamingText: "",
  chatStreamingReasoning: "",
  chatStreamingStatus: "",
  chatStreamingSteps: [],
  chatStreamingSources: [],
  chatPendingAttachments: [],
  publicModels: [],
  publicModelManagement: null,
  chatGptAccounts: [],
  chatGptFailoverEvents: [],
  pricingPlans: [],
  billingModelRates: null,
  adminOrders: [],
  adminEntitlements: [],
  adminMonthlyPassOperations: null
};

// ===== DOM 元素引用 =====
// 脚本以 type=module 加载(默认 defer),DOM 已就绪,可直接查询

export const els: Els = {
  navItems: document.querySelectorAll<HTMLElement>(".nav-item"),
  views: document.querySelectorAll<HTMLElement>(".view"),
  pageEyebrow: document.querySelector<HTMLElement>("#pageEyebrow"),
  pageTitle: document.querySelector<HTMLElement>("#pageTitle"),
  pageMeta: document.querySelector<HTMLElement>("#pageMeta"),
  modalBackdrop: document.querySelector<HTMLElement>("#modalBackdrop"),
  modals: document.querySelectorAll<HTMLElement>(".modal"),
  apiKeyModal: document.querySelector<HTMLElement>("#apiKeyModal"),
  createUserModal: document.querySelector<HTMLElement>("#createUserModal"),
  createUserForm: document.querySelector<HTMLFormElement>("#createUserForm"),
  rechargeUserModal: document.querySelector<HTMLElement>("#rechargeUserModal"),
  rechargeUserForm: document.querySelector<HTMLFormElement>("#rechargeUserForm"),
  policySettingsModal: document.querySelector<HTMLElement>("#policySettingsModal"),
  policySettingsForm: document.querySelector<HTMLFormElement>("#policySettingsForm"),
  policySettingsTarget: document.querySelector<HTMLElement>("#policySettingsTarget"),
  billingRateModal: document.querySelector<HTMLElement>("#billingRateModal"),
  billingRateForm: document.querySelector<HTMLFormElement>("#billingRateForm"),
  billingRateTarget: document.querySelector<HTMLElement>("#billingRateTarget"),
  pricingPlanModal: document.querySelector<HTMLElement>("#pricingPlanModal"),
  pricingPlanForm: document.querySelector<HTMLFormElement>("#pricingPlanForm"),
  pricingPlanModalTitle: document.querySelector<HTMLElement>("#pricingPlanModalTitle"),
  pricingPlanModalSubmit: document.querySelector<HTMLButtonElement>("#pricingPlanModalSubmit"),
  createModelModal: document.querySelector<HTMLElement>("#createModelModal"),
  createModelForm: document.querySelector<HTMLFormElement>("#createModelForm"),
  keyModal: document.querySelector<HTMLElement>("#keyModal"),
  apiKeyForm: document.querySelector<HTMLFormElement>("#apiKeyForm"),
  apiKeyName: document.querySelector<HTMLInputElement>("#apiKeyName"),
  memberList: document.querySelector<HTMLElement>("#memberList"),
  plainKey: document.querySelector<HTMLElement>("#plainKey"),
  refreshLogsButton: document.querySelector<HTMLElement>("#refreshLogsButton"),
  requestLogList: document.querySelector<HTMLElement>("#requestLogList"),
  teamOverview: document.querySelector<HTMLElement>("#teamOverview"),
  todayErrorRequests: document.querySelector<HTMLElement>("#todayErrorRequests"),
  accountContent: document.querySelector<HTMLElement>("#accountContent"),
  userStatus: document.querySelector<HTMLElement>("#userStatus"),
  layoutLanding: document.querySelector<HTMLElement>("#layout-landing"),
  layoutAuth: document.querySelector<HTMLElement>("#layout-auth"),
  layoutApp: document.querySelector<HTMLElement>("#layout-app"),
  layoutAdmin: document.querySelector<HTMLElement>("#layout-admin"),
  layoutChat: document.querySelector<HTMLElement>("#layout-chat"),
  landingGuestNav: document.querySelector<HTMLElement>("#landingGuestNav"),
  landingGuestChatEntry: document.querySelector<HTMLElement>("#landingGuestChatEntry"),
  landingUserBadge: document.querySelector<HTMLElement>("#landingUserBadge"),
  landingUserName: document.querySelector<HTMLElement>("#landingUserName"),
  landingConsoleLink: document.querySelector<HTMLButtonElement>("#landingConsoleLink"),
  authForm: document.querySelector<HTMLFormElement>("#authForm"),
  authEmail: document.querySelector<HTMLInputElement>("#authEmail"),
  authPassword: document.querySelector<HTMLInputElement>("#authPassword"),
  authPasswordConfirm: document.querySelector<HTMLInputElement>("#authPasswordConfirm"),
  authPasswordConfirmLabel: document.querySelector<HTMLElement>("#authPasswordConfirmLabel"),
  authName: document.querySelector<HTMLInputElement>("#authName"),
  authNameLabel: document.querySelector<HTMLElement>("#authNameLabel"),
  authError: document.querySelector<HTMLElement>("#authError"),
  authSubmitBtn: document.querySelector<HTMLButtonElement>("#authSubmitBtn"),
  appContent: document.querySelector<HTMLElement>("#appContent"),
  appUserBadge: document.querySelector<HTMLElement>("#appUserBadge"),
  appAdminLink: document.querySelector<HTMLElement>(".app-admin-link"),
  adminUsersContent: document.querySelector<HTMLElement>("#adminUsersContent"),
  adminUserDetailContent: document.querySelector<HTMLElement>("#adminUserDetailContent"),
  adminChannelsContent: document.querySelector<HTMLElement>("#adminChannelsContent"),
  adminWalletsContent: document.querySelector<HTMLElement>("#adminWalletsContent"),
  adminGptContent: document.querySelector<HTMLElement>("#adminGptContent"),
  adminHealthContent: document.querySelector<HTMLElement>("#adminHealthContent"),
  toast: document.querySelector<HTMLElement>("#toast"),
  refreshButton: document.querySelector<HTMLElement>("#refreshButton")
};
